import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Image as ImageIcon, LayoutTemplate, Instagram, Smartphone, Megaphone, Video, Clapperboard, Sparkles, Upload, X, Loader2, Wand2, CheckCircle2, XCircle, Home, Car, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { PageHeader, Spinner } from "@/components/app/common";
import { MediaView } from "@/components/app/Checkout";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, num, mediaUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

const CARDS = [
  { k: "image", t: "Görsel", icon: ImageIcon }, { k: "hd_image", t: "HD Görsel", icon: Sparkles },
  { k: "banner", t: "Banner", icon: LayoutTemplate }, { k: "instagram_post", t: "Instagram Post", icon: Instagram },
  { k: "instagram_story", t: "Story", icon: Smartphone }, { k: "ad_creative", t: "Reklam", icon: Megaphone },
  { k: "video", t: "Video", icon: Video }, { k: "reels", t: "Reels", icon: Clapperboard },
];
const isVideo = (k) => k === "video" || k === "reels";
const VIDEO_DURATION = 5;
const VIDEO_CATS = [
  { k: "emlak", t: "Emlak", icon: Home, ph: "Örn: Deniz manzaralı 3+1 satılık daire" },
  { k: "otomobil", t: "Otomobil", icon: Car, ph: "Örn: 2021 BMW 320i M Sport · 45.000 km" },
];
const EMPTY_VREQ = { category: "emlak", title: "", description: "", price: "", phone: "", location: "" };
const WAITING = ["QUEUED", "PROCESSING", "PREPARING"];

const VideoRequestForm = ({ value, onChange }) => {
  const cat = VIDEO_CATS.find((c) => c.k === value.category);
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="mt-5 space-y-4" data-testid="video-request-form">
      <div className="grid grid-cols-2 gap-3">
        {VIDEO_CATS.map((c) => (
          <button key={c.k} type="button" onClick={() => onChange({ ...value, category: c.k })} data-testid={`video-category-${c.k}`}
            className={cn("flex items-center gap-3 rounded-xl border p-3 text-left text-sm font-semibold transition-colors", value.category === c.k ? "border-primary bg-primary/10" : "border-border hover:border-primary/50")}>
            <c.icon className={cn("h-5 w-5", value.category === c.k ? "text-primary" : "text-muted-foreground")} />{c.t}
          </button>
        ))}
      </div>
      <Input value={value.title} onChange={set("title")} maxLength={120} placeholder={`İlan başlığı * (${cat.ph})`} className="rounded-xl" data-testid="video-title-input" />
      <Textarea value={value.description} onChange={set("description")} maxLength={2000} rows={3} placeholder="Açıklama (özellikler, metrekare, km, donanım...)" className="rounded-xl" data-testid="video-description-input" />
      <div className="grid gap-3 sm:grid-cols-3">
        <Input value={value.price} onChange={set("price")} maxLength={40} placeholder="Fiyat" className="rounded-xl" data-testid="video-price-input" />
        <Input value={value.phone} onChange={set("phone")} maxLength={25} placeholder="Telefon" className="rounded-xl" data-testid="video-phone-input" />
        <Input value={value.location} onChange={set("location")} maxLength={150} placeholder="Konum" className="rounded-xl" data-testid="video-location-input" />
      </div>
    </div>
  );
};

const Step = ({ n, title, children }) => (
  <section className="glass-card p-5 sm:p-7">
    <div className="mb-5 flex items-center gap-3"><span className="grid h-7 w-7 place-items-center rounded-full bg-primary/15 text-xs font-bold text-primary">{n}</span><h2 className="font-display text-lg font-semibold">{title}</h2></div>
    {children}
  </section>
);

const ResultBody = ({ gen }) => {
  if (gen.status === "COMPLETED") return <MediaView id={gen.output_file_id} type={gen.output_type} className="h-full w-full object-contain" />;
  if (["FAILED", "CANCELLED"].includes(gen.status)) return <div className="p-6 text-center"><XCircle className="mx-auto h-10 w-10 text-rose-500" /><p className="mt-3 text-sm" data-testid="generation-error">{gen.user_error}</p></div>;
  if (gen.status === "PREPARING") return <p className="p-6 text-center text-base font-semibold" data-testid="video-preparing-message">🔄 Videonuz hazırlanıyor...</p>;
  return <div className="p-6 text-center"><div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-primary border-t-transparent" /><p className="mt-4 text-sm font-semibold">{gen.status === "QUEUED" ? "Sırada bekliyor..." : "Yapay zekâ çalışıyor..."}</p><p className="mt-1 text-xs text-muted-foreground">Genellikle 10-40 saniye sürer.</p></div>;
};

export default function Create() {
  const { credits, setCredits } = useAuth();
  const nav = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [type, setType] = useState("image");
  const [prompt, setPrompt] = useState("");
  const [files, setFiles] = useState([]); // [{id, preview}]
  const [vreq, setVreq] = useState(EMPTY_VREQ);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gen, setGen] = useState(null);
  const timer = useRef(null);
  const video = isVideo(type);
  const MAX_FILES = video ? 5 : 1;

  useEffect(() => { api.get("/pricing").then(({ data }) => setPricing(data)); return () => clearTimeout(timer.current); }, []);
  useEffect(() => { setFiles((cur) => (cur.length > MAX_FILES ? cur.slice(0, MAX_FILES) : cur)); }, [MAX_FILES]);
  const rowFor = (k) => (pricing || []).find((r) => r.content_type === k && (isVideo(k) ? r.duration === VIDEO_DURATION : true));
  const row = rowFor(type);
  const cost = row?.customer_credit_cost ?? 0;
  const available = credits?.available ?? 0;
  const enough = available >= cost;

  const pick = async (list) => {
    const chosen = Array.from(list || []);
    if (!chosen.length) return;
    const room = MAX_FILES - files.length;
    if (chosen.length > room) toast.error(`En fazla ${MAX_FILES} fotoğraf yükleyebilirsiniz.`);
    setUploading(true);
    const added = [];
    for (const f of chosen.slice(0, Math.max(room, 0))) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) { toast.error(`${f.name}: yalnızca JPG, PNG veya WEBP.`); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: en fazla 10 MB.`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", f);
        let data;
        for (let a = 0; a < 3; a++) {
          try { ({ data } = await api.post("/media/upload", fd)); break; }
          catch (err) { if (a === 2 || (err?.response?.status || 500) < 500) throw err; await new Promise((r) => setTimeout(r, 1200 * (a + 1))); }
        }
        added.push({ id: data.id, preview: URL.createObjectURL(f) });
      } catch (e) { toast.error(errMsg(e)); }
    }
    setFiles((cur) => [...cur, ...added].slice(0, MAX_FILES));
    setUploading(false);
  };
  const move = (i, d) => setFiles((cur) => { const n = [...cur]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });

  const poll = (id) => {
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/generations/${id}`);
        setGen(data);
        if (WAITING.includes(data.status)) return poll(id);
        const me = await api.get("/auth/me");
        setCredits(me.data.credits);
        if (data.status === "COMPLETED") toast.success(data.kind === "video" ? "🎉 Videonuz hazır!" : "İçeriğiniz hazır!");
        else toast.error(data.status === "CANCELLED" ? "Video talebiniz iptal edildi, krediniz iade edildi." : "Üretim tamamlanamadı, krediniz iade edildi.");
      } catch { poll(id); }
    }, gen?.status === "PREPARING" || video ? 10000 : 4000);
  };

  const submit = async () => {
    if (!enough) return nav("/panel/satin-al");
    if (video && !vreq.title.trim()) return toast.error("İlan başlığını yazın.");
    if (!video && !prompt.trim() && !files.length) return toast.error("Lütfen bir açıklama yazın veya görsel yükleyin.");
    setBusy(true);
    try {
      const { data } = await api.post("/generations", { content_type: type, prompt: prompt.trim() || null, input_file_ids: files.map((f) => f.id), video: video ? vreq : null });
      setGen(data.generation);
      setCredits(data.credits);
      toast.success(video ? "Video talebiniz alındı." : `Üretim başladı · ${data.generation.customer_credit_cost} kredi rezerve edildi`);
      poll(data.generation.id);
      if (video) { setVreq(EMPTY_VREQ); setFiles([]); setPrompt(""); }
    } catch (e) {
      if (e?.response?.status === 402) { toast.error(errMsg(e)); nav("/panel/satin-al"); } else toast.error(errMsg(e));
    }
    setBusy(false);
  };

  if (!pricing) return <Spinner />;
  const running = gen && WAITING.includes(gen.status);
  const videoReady = gen?.kind === "video" && gen.status === "COMPLETED";
  return (
    <div data-testid="create-page">
      <PageHeader title="İçerik Oluştur" desc="Türü seçin, isterseniz açıklama yazın veya görsel yükleyin." />
      <div className="grid gap-6 lg:grid-cols-[1.35fr_1fr]">
        <div className="space-y-6">
          <Step n="1" title="Ne oluşturmak istiyorsunuz?">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {CARDS.map((c) => {
                const r = rowFor(c.k);
                return (
                  <button key={c.k} disabled={!r} onClick={() => setType(c.k)} data-testid={`content-type-${c.k}`}
                    className={cn("group rounded-2xl border p-4 text-left transition-all disabled:opacity-40", type === c.k ? "border-primary bg-primary/10 shadow-[0_0_24px_hsl(var(--primary)/.2)]" : "border-border hover:-translate-y-0.5 hover:border-primary/50")}>
                    <c.icon className={cn("h-5 w-5 transition-transform group-hover:scale-110", type === c.k ? "text-primary" : "text-muted-foreground")} />
                    <div className="mt-3 text-sm font-semibold">{c.t}</div>
                    <div className="text-xs text-muted-foreground">{r ? `${r.customer_credit_cost} kredi` : "Pasif"}</div>
                  </button>
                );
              })}
            </div>
            {video && <VideoRequestForm value={vreq} onChange={setVreq} />}
          </Step>
          <Step n="2" title={video ? "Video için ek açıklama / istek (opsiyonel)" : "Ne oluşturmak istiyorsunuz? (opsiyonel)"}>
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={2000} rows={4} data-testid="prompt-input" className="rounded-xl"
              placeholder={video ? "Örn: Müzikli, dinamik geçişli bir tanıtım videosu olsun" : "Örn: Kahve markam için sonbahar temalı, sıcak tonlarda bir kampanya görseli"} />
            <div className="mt-1 text-right text-xs text-muted-foreground">{prompt.length}/2000</div>
          </Step>
          <Step n="3" title={`${video ? "Fotoğraf" : "Görsel"} yükleyin (opsiyonel · en fazla ${MAX_FILES})`}>
            {files.length > 0 && (
              <div className="mb-4 grid grid-cols-3 gap-3 sm:grid-cols-5" data-testid="upload-preview-list">
                {files.map((f, i) => (
                  <div key={f.id} className="relative">
                    <img src={f.preview} alt={`Yüklenen görsel ${i + 1}`} className="aspect-square w-full rounded-xl object-cover" data-testid={`upload-preview-${i}`} />
                    <button onClick={() => setFiles(files.filter((x) => x.id !== f.id))} className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-rose-500 text-white" data-testid={`upload-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
                    {video && files.length > 1 && (
                      <div className="absolute inset-x-1 bottom-1 flex justify-between">
                        <button disabled={i === 0} onClick={() => move(i, -1)} className="grid h-6 w-6 place-items-center rounded-full bg-black/60 text-white disabled:opacity-0" data-testid={`upload-move-left-${i}`}><ChevronLeft className="h-4 w-4" /></button>
                        <span className="rounded-full bg-black/60 px-2 text-xs leading-6 text-white">{i + 1}</span>
                        <button disabled={i === files.length - 1} onClick={() => move(i, 1)} className="grid h-6 w-6 place-items-center rounded-full bg-black/60 text-white disabled:opacity-0" data-testid={`upload-move-right-${i}`}><ChevronRight className="h-4 w-4" /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {files.length < MAX_FILES && (
              <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border px-4 py-8 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-foreground">
                {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <Upload className="h-6 w-6" />}
                JPG, PNG veya WEBP · görsel başına en fazla 10 MB · {files.length}/{MAX_FILES}
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple={video} className="hidden" onChange={(e) => { pick(e.target.files); e.target.value = ""; }} data-testid="image-upload-input" />
              </label>
            )}
          </Step>
        </div>
        <div className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <section className="glass-card p-6" data-testid="generate-panel">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Kullanılacak kredi:</span><b data-testid="generate-cost">{num(cost)}</b></div>
            <div className="mt-2 flex justify-between text-sm"><span className="text-muted-foreground">Kalan kredi:</span><b data-testid="generate-available">{num(available)}</b></div>
            {!enough && <p className="mt-3 text-xs text-rose-500" data-testid="insufficient-credit-warning">Bu {video ? "talep" : "üretim"} için yeterli krediniz yok.</p>}
            <Button onClick={submit} disabled={busy || uploading || !row} className="btn-glow mt-5 h-12 w-full rounded-xl text-base font-bold" data-testid="generate-content-button">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-5 w-5" />}{!enough ? "Kredi satın al" : video ? "VİDEO TALEBİ OLUŞTUR" : "ÜRET"}
            </Button>
          </section>
          {gen && (
            <section className="glass-card overflow-hidden" data-testid="generation-result">
              {videoReady && <div className="bg-emerald-500/15 p-4 text-center font-semibold text-emerald-500" data-testid="video-ready-banner">🎉 Videonuz hazır!</div>}
              <div className="grid aspect-square place-items-center bg-muted/30"><ResultBody gen={gen} /></div>
              <div className="flex items-center justify-between gap-3 p-4 text-sm" data-testid="generation-status">
                <span className="flex items-center gap-2 font-semibold">{gen.status === "COMPLETED" && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}{gen.label}</span>
                {videoReady ? <a href={mediaUrl(gen.output_file_id)} download className="flex items-center gap-1 text-primary" data-testid="video-download-link"><Download className="h-4 w-4" />Videoyu İndir</a>
                  : !running || gen.status === "PREPARING" ? <Link to="/panel/galeri" className="text-primary">Galeride gör</Link> : null}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
